// src/context/AuthContext.tsx
"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { resolveClientSubdomain } from "@/lib/api";

export interface AuthUser {
  id: number | string;
  email: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  phone?: string;
  role?: string;
  subdomain?: string;
}

export interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (token: string, userData: AuthUser) => void;
  logout: () => void;
  refreshProfile: () => Promise<void>;
  isAuthModalOpen: boolean;
  authModalTab: "login" | "register";
  openAuthModal: (tab?: "login" | "register") => void;
  closeAuthModal: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  login: () => {},
  logout: () => {},
  refreshProfile: async () => {},
  isAuthModalOpen: false,
  authModalTab: "login",
  openAuthModal: () => {},
  closeAuthModal: () => {},
});

function detectSubdomain(): string {
  return resolveClientSubdomain();
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<"login" | "register">("login");

  const openAuthModal = useCallback((tab: "login" | "register" = "login") => {
    setAuthModalTab(tab);
    setIsAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setIsAuthModalOpen(false);
  }, []);

  const loadLocalUser = (): { storedToken: string | null; storedUser: AuthUser | null } => {
    if (typeof window === "undefined") return { storedToken: null, storedUser: null };
    const sub = detectSubdomain();

    const storedToken =
      (sub && localStorage.getItem(`xylo_token_${sub}`)) ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("token");

    const rawUserStr =
      (sub && localStorage.getItem(`xylo_user_${sub}`)) ||
      (sub && localStorage.getItem(`customer_info_${sub}`)) ||
      localStorage.getItem("xylo_user") ||
      localStorage.getItem("customer_info");

    let storedUser: AuthUser | null = null;
    if (rawUserStr) {
      try {
        storedUser = JSON.parse(rawUserStr);
      } catch {
        storedUser = null;
      }
    }
    return { storedToken, storedUser };
  };

  const refreshProfile = async () => {
    if (typeof window === "undefined") return;
    const sub = detectSubdomain();
    const { storedToken, storedUser } = loadLocalUser();

    if (!storedToken && !storedUser?.email) {
      setLoading(false);
      return;
    }

    const headers: Record<string, string> = {};
    if (sub) headers["X-Tenant-Subdomain"] = sub;
    if (storedToken) headers["Authorization"] = `Bearer ${storedToken}`;

    // যদি ব্রাউজারে ইউজার অবজেক্ট থাকে কিন্তু টোকেন মিসিং থাকে, তবে ব্যাকএন্ড থেকে অটো টোকেন সিঙ্ক করবে
    if (!storedToken && storedUser?.email) {
      try {
        const cleanEmail = storedUser.email.trim().toLowerCase();
        const fName = storedUser.first_name || storedUser.name?.split(" ")[0] || cleanEmail.split("@")[0];
        const lName = storedUser.last_name || storedUser.name?.split(" ").slice(1).join(" ") || "";
        const syncRes = await api.post(
          "/api/v1/customers/google/",
          {
            email: cleanEmail,
            name: storedUser.name || `${fName} ${lName}`.trim(),
            first_name: fName,
            last_name: lName,
            phone: storedUser.phone || "",
            subdomain: sub,
          },
          { headers }
        );
        const newAccess = syncRes.data?.tokens?.access || syncRes.data?.access || syncRes.data?.token;
        if (newAccess) {
          setToken(newAccess);
          headers["Authorization"] = `Bearer ${newAccess}`;
          if (sub) localStorage.setItem(`xylo_token_${sub}`, newAccess);
          localStorage.setItem("access_token", newAccess);
          localStorage.setItem("token", newAccess);
        }
      } catch {
        // Ignore auto-sync failure
      }
    }

    try {
      const emailParam = storedUser?.email ? `?email=${encodeURIComponent(storedUser.email)}` : "";
      const res = await api.get(`/api/v1/customers/me/${emailParam}`, { headers });
      const data = res.data?.customer || res.data?.user || res.data;

      if (data && data.email) {
        const cleanEmail = String(data.email).trim().toLowerCase();
        const updatedUser: AuthUser = {
          id: data.id || storedUser?.id || Date.now(),
          email: cleanEmail,
          first_name: data.first_name || storedUser?.first_name || "",
          last_name: data.last_name || storedUser?.last_name || "",
          name:
            data.name ||
            `${data.first_name || ""} ${data.last_name || ""}`.trim() ||
            storedUser?.name ||
            cleanEmail.split("@")[0],
          phone: data.phone || storedUser?.phone || "",
          role: data.role || "customer",
          subdomain: sub,
        };

        setUser(updatedUser);
        if (sub) {
          localStorage.setItem(`xylo_user_${sub}`, JSON.stringify(updatedUser));
          localStorage.setItem(`customer_info_${sub}`, JSON.stringify(updatedUser));
        }
        localStorage.setItem("xylo_user", JSON.stringify(updatedUser));
        localStorage.setItem("customer_info", JSON.stringify(updatedUser));
      }
    } catch {
      // Keep existing local user on network failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const { storedToken, storedUser } = loadLocalUser();
    if (storedToken) setToken(storedToken);
    if (storedUser) setUser(storedUser);
    refreshProfile();
  }, []);

  const login = (newToken: string, userData: AuthUser) => {
    const sub = detectSubdomain();
    const normalizedUser: AuthUser = {
      ...userData,
      email: (userData.email || "").trim().toLowerCase(),
      name:
        userData.name ||
        `${userData.first_name || ""} ${userData.last_name || ""}`.trim() ||
        (userData.email || "").split("@")[0],
      subdomain: sub,
    };

    setToken(newToken);
    setUser(normalizedUser);

    if (typeof window !== "undefined") {
      if (sub) {
        localStorage.setItem(`xylo_token_${sub}`, newToken);
        localStorage.setItem(`xylo_user_${sub}`, JSON.stringify(normalizedUser));
        localStorage.setItem(`customer_info_${sub}`, JSON.stringify(normalizedUser));
      }
      localStorage.setItem("access_token", newToken);
      localStorage.setItem("token", newToken);
      localStorage.setItem("xylo_user", JSON.stringify(normalizedUser));
      localStorage.setItem("customer_info", JSON.stringify(normalizedUser));
    }
  };

  const logout = () => {
    const sub = detectSubdomain();
    setToken(null);
    setUser(null);

    if (typeof window !== "undefined") {
      if (sub) {
        localStorage.removeItem(`xylo_token_${sub}`);
        localStorage.removeItem(`xylo_refresh_${sub}`);
        localStorage.removeItem(`xylo_user_${sub}`);
        localStorage.removeItem(`customer_info_${sub}`);
      }
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("token");
      localStorage.removeItem("xylo_user");
      localStorage.removeItem("customer_info");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user && !!(token || user.email),
        loading,
        login,
        logout,
        refreshProfile,
        isAuthModalOpen,
        authModalTab,
        openAuthModal,
        closeAuthModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);